import React, { useState, useEffect, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import PropTypes from "prop-types";
import axios from "axios";
import parse from "html-react-parser";
import { motion, AnimatePresence } from "framer-motion";

import {
  Newspaper,
  CalendarDays,
  Clock,
  ArrowLeft,
  ArrowRight,
  Search,
  Share2,
  ChevronRight,
  GraduationCap,
  BookOpen,
  X,
  ExternalLink,
  RefreshCw,
  AlertCircle,
} from "lucide-react";

import LogoFallback from "/assets/img/bubbanlogo.jpg";

const API_BASE = import.meta.env.VITE_API_BASE_URL;
const API_SOURCE = import.meta.env.VITE_API_SOURCE_URL;

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.08 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 18 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.4, ease: "easeOut" },
  },
};

function formatDate(dateStr) {
  if (!dateStr) return "Date unavailable";

  const match = String(dateStr).match(/^(\d{4})-(\d{2})-(\d{2})/);

  const date = match
    ? new Date(+match[1], +match[2] - 1, +match[3])
    : new Date(dateStr);

  if (Number.isNaN(date.getTime())) return String(dateStr);

  return date.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

function getImageUrl(path) {
  if (!path) return "";

  if (/^https?:\/\//i.test(path)) return path;

  return `${API_SOURCE || ""}/${String(path).replace(/^\/+/, "")}`;
}

function getNewsRecord(data) {
  if (Array.isArray(data)) return data[0] || null;

  if (data && Array.isArray(data.data)) {
    return data.data[0] || null;
  }

  if (data && typeof data === "object") return data;

  return null;
}

export default function NewsDetails({
  logoSrc = LogoFallback,
  accent = "#115e32",
  pageTitle = "News | Bandarban University",
}) {
  const { id } = useParams();

  const [allnews, setAllnews] = useState([]);
  const [selectedNews, setSelectedNews] = useState(null);

  const [loading, setLoading] = useState(true);
  const [recentLoading, setRecentLoading] = useState(true);
  const [error, setError] = useState("");
  const [recentError, setRecentError] = useState("");

  const [search, setSearch] = useState("");
  const [imageOpen, setImageOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  // Fetch selected news whenever the route ID changes.
  useEffect(() => {
    let cancelled = false;

    async function fetchNews() {
      setLoading(true);
      setError("");
      setSelectedNews(null);
      setImageOpen(false);

      try {
        const response = await axios.post(
          `${API_BASE}/news-details`,
          {
            userId: import.meta.env.VITE_API_USER,
            password: import.meta.env.VITE_API_PASS,
            id,
          },
          {
            headers: { "Content-Type": "application/json" },
          }
        );

        if (cancelled) return;

        const record = getNewsRecord(response.data);

        if (!record) {
          setError("The requested news article could not be found.");
          return;
        }

        setSelectedNews(record);
      } catch (err) {
        if (!cancelled) {
          setError(
            err.response?.data?.message ||
              err.message ||
              "Unable to load this news article."
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    if (id) {
      fetchNews();
    } else {
      setLoading(false);
      setError("News ID is missing.");
    }

    return () => {
      cancelled = true;
    };
  }, [id]);

  // Fetch recent news independently of the selected article.
  useEffect(() => {
    let cancelled = false;

    async function fetchRecentNews() {
      setRecentLoading(true);
      setRecentError("");

      try {
        const response = await axios.post(
          `${API_BASE}/recent-news`,
          {
            userId: import.meta.env.VITE_API_USER,
            password: import.meta.env.VITE_API_PASS,
            department_id: "0",
          },
          {
            headers: { "Content-Type": "application/json" },
          }
        );

        if (cancelled) return;

        const data = response.data;
        const records = Array.isArray(data)
          ? data
          : Array.isArray(data?.data)
            ? data.data
            : [];

        setAllnews(records);
      } catch (err) {
        if (!cancelled) {
          setRecentError(
            err.response?.data?.message ||
              "Unable to load recent news at this time."
          );
        }
      } finally {
        if (!cancelled) setRecentLoading(false);
      }
    }

    fetchRecentNews();

    return () => {
      cancelled = true;
    };
  }, []);

  // Update document title.
  useEffect(() => {
    document.title = selectedNews?.head_line
      ? `${selectedNews.head_line} | Bandarban University`
      : pageTitle;
  }, [selectedNews, pageTitle]);

  // Search recent news.
  const filteredNews = useMemo(() => {
    const query = search.trim().toLowerCase();

    return allnews
      .filter((item) => String(item.id) !== String(id))
      .filter((item) =>
        String(item.head_line || "").toLowerCase().includes(query)
      )
      .slice(0, 15);
  }, [allnews, search, id]);

  const imageUrl = getImageUrl(selectedNews?.images_path);

  async function handleShare() {
    const url = window.location.href;

    try {
      if (navigator.share) {
        await navigator.share({
          title: selectedNews?.head_line || "University News",
          url,
        });
      } else if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
        setCopied(true);
        window.setTimeout(() => setCopied(false), 2500);
      } else {
        window.prompt("Copy this news URL:", url);
      }
    } catch (err) {
      if (err.name !== "AbortError") {
        setError("Unable to share this article.");
      }
    }
  }

  return (
    <main
      className="min-h-screen overflow-x-clip bg-slate-50 text-slate-800"
      style={{ "--news-accent": accent }}
    >
      {/* ===================================================
          HERO SECTION
      =================================================== */}
      <section className="relative isolate overflow-hidden bg-gradient-to-br from-green-950 via-green-800 to-emerald-700 text-white">
        {/* Animated background */}
        <div className="pointer-events-none absolute inset-0 overflow-hidden">
          <motion.div
            animate={{
              x: [0, 30, 0],
              y: [0, -20, 0],
              scale: [1, 1.1, 1],
            }}
            transition={{
              duration: 12,
              repeat: Infinity,
              ease: "easeInOut",
            }}
            className="absolute -right-24 -top-28 h-96 w-96 rounded-full bg-emerald-300/15 blur-3xl"
          />

          <motion.div
            animate={{ x: [0, -25, 0], y: [0, 20, 0] }}
            transition={{
              duration: 10,
              repeat: Infinity,
              ease: "easeInOut",
            }}
            className="absolute -bottom-40 left-1/4 h-96 w-96 rounded-full bg-teal-300/15 blur-3xl"
          />

          <div
            className="absolute inset-0 opacity-[0.07]"
            style={{
              backgroundImage:
                "radial-gradient(circle, white 1px, transparent 1px)",
              backgroundSize: "25px 25px",
            }}
          />
        </div>

        <div className="relative mx-auto max-w-7xl px-5 pb-20 pt-8 sm:px-8 sm:pb-24 sm:pt-10 lg:px-10 lg:pb-28">
          {/* Breadcrumb */}
          <motion.nav
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            aria-label="Breadcrumb"
            className="mb-12 flex flex-wrap items-center gap-2 text-sm text-green-50/75"
          >
            <Link to="/" className="transition hover:text-white">
              Home
            </Link>

            <ChevronRight size={15} />

            <Link
              to="/all-news"
              className="transition hover:text-white"
            >
              News
            </Link>

            <ChevronRight size={15} />

            <span className="text-emerald-200">News Details</span>
          </motion.nav>

          <div className="grid items-center gap-10 lg:grid-cols-[1fr_auto]">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.65 }}
              className="min-w-0"
            >
              <span className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-4 py-2 text-xs font-bold uppercase tracking-[0.15em] backdrop-blur-md sm:text-sm">
                <Newspaper size={16} className="text-emerald-300" />
                University News & Updates
              </span>

              <AnimatePresence mode="wait">
                {loading ? (
                  <motion.div
                    key="hero-loading"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="mt-7 space-y-4"
                  >
                    <div className="h-10 w-4/5 animate-pulse rounded-xl bg-white/15" />
                    <div className="h-10 w-3/5 animate-pulse rounded-xl bg-white/10" />
                    <div className="h-4 w-48 animate-pulse rounded bg-white/10" />
                  </motion.div>
                ) : (
                  <motion.div
                    key={selectedNews?.id || "hero-error"}
                    initial={{ opacity: 0, y: 15 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <h1 className="mt-7 max-w-4xl break-words text-3xl font-black leading-tight tracking-tight sm:text-4xl md:text-5xl lg:text-6xl">
                      {selectedNews?.head_line || "News & Updates"}
                    </h1>

                    <p className="mt-5 max-w-2xl text-sm leading-7 text-green-50/85 sm:text-lg">
                      Discover the latest achievements, announcements,
                      activities, and stories from Bandarban University.
                    </p>
                  </motion.div>
                )}
              </AnimatePresence>

              <div className="mt-7 flex flex-wrap items-center gap-3">
                <span className="inline-flex items-center gap-2 rounded-xl border border-white/15 bg-white/10 px-4 py-3 text-sm font-medium backdrop-blur-md">
                  <CalendarDays
                    size={17}
                    className="text-emerald-300"
                  />
                  {selectedNews
                    ? formatDate(selectedNews.date)
                    : "Loading date..."}
                </span>

                <button
                  type="button"
                  onClick={handleShare}
                  disabled={!selectedNews}
                  className="inline-flex items-center gap-2 rounded-xl border border-white/20 bg-white/10 px-4 py-3 text-sm font-semibold transition hover:bg-white/20 disabled:opacity-50"
                >
                  <Share2 size={16} />
                  {copied ? "Link Copied!" : "Share News"}
                </button>
              </div>
            </motion.div>

            {/* Floating logo */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8, rotate: -8 }}
              animate={{ opacity: 1, scale: 1, rotate: 0 }}
              transition={{ duration: 0.7, delay: 0.15 }}
              className="hidden justify-center lg:flex"
            >
              <motion.div
                animate={{ y: [0, -8, 0] }}
                transition={{ duration: 4, repeat: Infinity }}
                className="relative flex h-48 w-48 items-center justify-center rounded-[2rem] border border-white/20 bg-white/10 p-7 shadow-2xl backdrop-blur-xl xl:h-56 xl:w-56"
              >
                <div className="absolute inset-3 rounded-[1.5rem] border border-white/10" />

                <img
                  src={logoSrc}
                  alt="Bandarban University Logo"
                  className="relative h-full w-full object-contain drop-shadow-lg"
                />
              </motion.div>
            </motion.div>
          </div>
        </div>

        {/* Bottom wave */}
        <div className="absolute bottom-0 left-0 w-full">
          <svg
            viewBox="0 0 1440 80"
            preserveAspectRatio="none"
            xmlns="http://www.w3.org/2000/svg"
            className="block h-10 w-full sm:h-16"
          >
            <path
              d="M0,40 C240,90 480,0 720,35 C960,70 1200,15 1440,35 L1440 80 L0 80 Z"
              fill="#f8fafc"
            />
          </svg>
        </div>
      </section>

      {/* ===================================================
          MAIN CONTENT
      =================================================== */}
      <section className="relative mx-auto max-w-7xl px-4 py-10 sm:px-8 sm:py-14 lg:px-10 lg:py-16">
        <div className="grid min-w-0 grid-cols-1 items-start gap-8 lg:grid-cols-3">
          {/* LEFT: NEWS ARTICLE */}
          <motion.article
            initial={{ opacity: 0, y: 25 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55 }}
            className="min-w-0 overflow-hidden rounded-3xl border border-slate-200/80 bg-white shadow-[0_12px_50px_rgba(15,23,42,0.055)] lg:col-span-2"
          >
            {/* Article header */}
            <div className="border-b border-slate-100 p-5 sm:p-8">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <span className="inline-flex items-center gap-2 rounded-full bg-green-50 px-3 py-2 text-xs font-bold uppercase tracking-wider text-green-800">
                  <BookOpen size={15} />
                  University Story
                </span>

                {selectedNews && (
                  <span className="flex items-center gap-2 text-xs text-slate-500">
                    <Clock size={14} />
                    {formatDate(selectedNews.date)}
                  </span>
                )}
              </div>

              <AnimatePresence mode="wait">
                {loading ? (
                  <motion.div
                    key="article-loading"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="mt-6 space-y-4"
                  >
                    <div className="h-7 w-4/5 animate-pulse rounded-lg bg-slate-100" />
                    <div className="h-4 w-2/5 animate-pulse rounded-lg bg-slate-100" />
                  </motion.div>
                ) : error ? (
                  <motion.div
                    key="article-error"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="mt-6 rounded-2xl border border-red-100 bg-red-50 p-6"
                  >
                    <AlertCircle size={30} className="text-red-600" />

                    <h2 className="mt-3 font-bold text-red-900">
                      Unable to load news
                    </h2>

                    <p className="mt-2 break-words text-sm leading-6 text-red-700">
                      {error}
                    </p>

                    <button
                      type="button"
                      onClick={() => window.location.reload()}
                      className="mt-4 inline-flex items-center gap-2 rounded-lg bg-red-700 px-4 py-2.5 text-sm font-bold text-white hover:bg-red-800"
                    >
                      <RefreshCw size={15} />
                      Try Again
                    </button>
                  </motion.div>
                ) : selectedNews ? (
                  <motion.div
                    key={`article-${selectedNews.id || id}`}
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -8 }}
                    transition={{ duration: 0.3 }}
                  >
                    <h2 className="mt-6 break-words text-2xl font-black leading-snug text-slate-900 sm:text-3xl">
                      {selectedNews.head_line}
                    </h2>

                    <div className="mt-4 flex flex-wrap items-center gap-3 text-sm text-slate-500">
                      <span className="flex items-center gap-2">
                        <CalendarDays
                          size={16}
                          className="text-green-700"
                        />
                        {formatDate(selectedNews.date)}
                      </span>

                      <span className="h-1 w-1 rounded-full bg-slate-300" />

                      <span className="flex items-center gap-2">
                        <GraduationCap
                          size={17}
                          className="text-green-700"
                        />
                        Bandarban University
                      </span>
                    </div>
                  </motion.div>
                ) : null}
              </AnimatePresence>
            </div>

            {/* Article content */}
            {!loading && !error && selectedNews && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.45, delay: 0.1 }}
                className="p-5 sm:p-8"
              >
                {/* Feature image */}
                {selectedNews.images_path && (
                  <div className="group relative mb-8 overflow-hidden rounded-2xl bg-slate-100">
                    <button
                      type="button"
                      onClick={() => setImageOpen(true)}
                      aria-label="Enlarge news image"
                      className="block w-full cursor-zoom-in"
                    >
                      <img
                        src={imageUrl}
                        alt={selectedNews.head_line || "University news"}
                        loading="lazy"
                        className="max-h-[600px] min-h-[180px] w-full object-cover transition-transform duration-700 group-hover:scale-[1.025] sm:min-h-[280px]"
                      />

                      <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/35 via-transparent to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100" />

                      <span className="absolute bottom-4 right-4 flex translate-y-2 items-center gap-2 rounded-xl bg-white/95 px-3 py-2 text-xs font-bold text-slate-800 opacity-0 shadow-lg transition-all duration-300 group-hover:translate-y-0 group-hover:opacity-100">
                        <ExternalLink size={14} />
                        Enlarge Image
                      </span>
                    </button>
                  </div>
                )}

                {/* Description */}
                {selectedNews.description ? (
                  <div className="mb-8 rounded-2xl border border-green-100 bg-gradient-to-br from-green-50/70 to-white p-5 sm:p-7">
                    <div className="mb-4 flex items-center gap-3">
                      <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-green-800 text-white">
                        <Newspaper size={18} />
                      </span>

                      <h3 className="font-extrabold text-green-950">
                        News & Highlights
                      </h3>
                    </div>

                    <div className="news-article-content break-words text-sm leading-8 text-slate-700 sm:text-base [&_a]:font-semibold [&_a]:text-green-800 [&_a]:underline [&_blockquote]:my-5 [&_blockquote]:border-l-4 [&_blockquote]:border-green-500 [&_blockquote]:pl-4 [&_h2]:mb-3 [&_h2]:mt-6 [&_h2]:text-2xl [&_h2]:font-bold [&_h3]:mb-2 [&_h3]:mt-5 [&_h3]:text-xl [&_h3]:font-bold [&_img]:my-5 [&_img]:h-auto [&_img]:max-w-full [&_li]:ml-5 [&_li]:list-disc [&_ol]:my-4 [&_p]:mb-4 [&_strong]:font-bold [&_ul]:my-4">
                      {parse(
                        String(selectedNews.description)
                          .replace(/\\n/g, "\n")
                          .replace(/\n/g, "<br />")
                      )}
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-slate-500">
                    No additional news description is available.
                  </p>
                )}

                {/* Article footer */}
                <div className="flex flex-wrap items-center justify-between gap-4 border-t border-slate-100 pt-6">
                  <Link
                    to="/all-news"
                    className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-bold text-slate-700 transition hover:border-green-200 hover:bg-green-50 hover:text-green-800"
                  >
                    <ArrowLeft size={17} />
                    All News
                  </Link>

                  <button
                    type="button"
                    onClick={handleShare}
                    className="inline-flex items-center gap-2 rounded-xl bg-green-800 px-4 py-3 text-sm font-bold text-white transition hover:bg-green-900"
                  >
                    <Share2 size={16} />
                    {copied ? "Link Copied!" : "Share Article"}
                  </button>
                </div>
              </motion.div>
            )}
          </motion.article>

          {/* RIGHT: RECENT NEWS */}
          <motion.aside
            initial={{ opacity: 0, x: 25 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.55, delay: 0.1 }}
            className="min-w-0 overflow-hidden rounded-3xl border border-slate-200/80 bg-white shadow-[0_12px_50px_rgba(15,23,42,0.055)]"
          >
            {/* Sidebar header */}
            <div className="relative overflow-hidden bg-gradient-to-br from-green-900 via-green-800 to-emerald-600 p-5 text-white sm:p-6">
              <div className="absolute -right-8 -top-8 h-28 w-28 rounded-full bg-white/10 blur-xl" />

              <div className="relative flex items-center justify-between gap-3">
                <div>
                  <span className="text-xs font-bold uppercase tracking-[0.15em] text-green-100">
                    Stay Connected
                  </span>

                  <h2 className="mt-2 text-xl font-black">
                    Recent News
                  </h2>

                  <p className="mt-1 text-xs text-green-50/75">
                    Latest stories and updates
                  </p>
                </div>

                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl border border-white/20 bg-white/10">
                  <Newspaper size={22} />
                </div>
              </div>

              <div className="relative mt-5 flex items-center gap-2 rounded-xl border border-white/15 bg-white/10 px-3 py-2.5">
                <BookOpen size={17} className="shrink-0 text-green-100" />
                <span className="text-sm font-semibold">
                  {allnews.length} news articles
                </span>
              </div>
            </div>

            {/* Search field */}
            <div className="border-b border-slate-100 p-4">
              <div className="relative">
                <Search
                  size={17}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                />

                <input
                  type="search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search news..."
                  aria-label="Search recent news"
                  className="w-full rounded-xl border border-slate-200 bg-slate-50 py-3 pl-10 pr-9 text-sm outline-none transition focus:border-green-600 focus:bg-white focus:ring-4 focus:ring-green-100"
                />

                {search && (
                  <button
                    type="button"
                    onClick={() => setSearch("")}
                    aria-label="Clear search"
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
                  >
                    <X size={15} />
                  </button>
                )}
              </div>
            </div>

            {/* News list */}
            <div className="p-3 sm:p-4">
              {recentLoading ? (
                <div className="space-y-3 p-2">
                  {[1, 2, 3, 4, 5].map((item) => (
                    <div key={item} className="flex gap-3 rounded-xl p-3">
                      <div className="h-14 w-14 shrink-0 animate-pulse rounded-xl bg-slate-100" />

                      <div className="min-w-0 flex-1 space-y-2 py-1">
                        <div className="h-3 w-1/3 animate-pulse rounded bg-slate-100" />
                        <div className="h-4 w-full animate-pulse rounded bg-slate-100" />
                        <div className="h-3 w-3/4 animate-pulse rounded bg-slate-100" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : recentError ? (
                <div className="p-6 text-center">
                  <AlertCircle
                    size={28}
                    className="mx-auto text-amber-600"
                  />

                  <p className="mt-3 text-sm leading-6 text-slate-600">
                    {recentError}
                  </p>
                </div>
              ) : filteredNews.length > 0 ? (
                <motion.div
                  variants={containerVariants}
                  initial="hidden"
                  animate="visible"
                  className="space-y-1"
                >
                  {filteredNews.map((item) => (
                    <motion.div
                      key={item.id}
                      variants={itemVariants}
                      whileHover={{ x: 3 }}
                    >
                      <Link
                        to={`/news/${item.id}`}
                        className="group flex min-w-0 gap-3 rounded-xl border border-transparent p-3 transition-all duration-200 hover:border-green-100 hover:bg-green-50/80"
                      >
                        {/* Thumbnail */}
                        <div className="relative h-[65px] w-[76px] shrink-0 overflow-hidden rounded-xl bg-slate-100">
                          {item.images_path ? (
                            <img
                              src={getImageUrl(item.images_path)}
                              alt=""
                              loading="lazy"
                              className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110"
                            />
                          ) : (
                            <div className="flex h-full w-full items-center justify-center text-green-700">
                              <Newspaper size={23} />
                            </div>
                          )}

                          <div className="absolute inset-0 bg-green-950/0 transition-colors group-hover:bg-green-950/10" />
                        </div>

                        {/* News title */}
                        <div className="min-w-0 flex-1">
                          <p className="mb-1 text-[10px] font-bold uppercase tracking-wider text-slate-400">
                            {formatDate(item.date)}
                          </p>

                          <h3 className="line-clamp-3 break-words text-sm font-semibold leading-5 text-slate-700 transition-colors group-hover:text-green-800">
                            {item.head_line}
                          </h3>
                        </div>

                        <ChevronRight
                          size={16}
                          className="mt-5 shrink-0 text-slate-300 transition-all group-hover:translate-x-1 group-hover:text-green-700"
                        />
                      </Link>
                    </motion.div>
                  ))}
                </motion.div>
              ) : (
                <div className="p-8 text-center">
                  <Search size={30} className="mx-auto text-slate-300" />

                  <p className="mt-3 text-sm font-semibold text-slate-700">
                    No news found
                  </p>

                  <p className="mt-1 text-xs text-slate-500">
                    Try another search keyword.
                  </p>
                </div>
              )}

              {/* View all */}
              <div className="mt-4 border-t border-slate-100 pt-4">
                <Link
                  to="/all-news"
                  className="group flex items-center justify-center gap-2 rounded-xl bg-green-50 px-4 py-3 text-sm font-bold text-green-800 transition hover:bg-green-800 hover:text-white"
                >
                  View All News
                  <ArrowRight
                    size={16}
                    className="transition-transform group-hover:translate-x-1"
                  />
                </Link>
              </div>
            </div>
          </motion.aside>
        </div>

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="relative mt-10 overflow-hidden rounded-3xl border border-green-100 bg-gradient-to-r from-green-50 via-white to-emerald-50 p-5 sm:p-7"
        >
          <div className="flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-4">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-green-800 text-white shadow-lg">
                <GraduationCap size={24} />
              </div>

              <div>
                <h2 className="font-extrabold text-slate-900">
                  Bandarban University
                </h2>

                <p className="mt-1 text-sm text-slate-500">
                  Discover our latest news and achievements.
                </p>
              </div>
            </div>

            <Link
              to="/all-news"
              className="inline-flex items-center justify-center gap-2 rounded-xl border border-green-200 bg-white px-5 py-3 text-sm font-bold text-green-800 transition hover:bg-green-800 hover:text-white"
            >
              Explore All News
              <ArrowRight size={17} />
            </Link>
          </div>
        </motion.div>
      </section>

      {/* Image lightbox */}
      <AnimatePresence>
        {imageOpen && imageUrl && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-950/90 p-4 backdrop-blur-sm"
            onClick={() => setImageOpen(false)}
          >
            <button
              type="button"
              aria-label="Close image preview"
              onClick={() => setImageOpen(false)}
              className="absolute right-5 top-5 rounded-xl bg-white/10 p-3 text-white transition hover:bg-white/20"
            >
              <X size={24} />
            </button>

            <motion.img
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              transition={{ duration: 0.25 }}
              src={imageUrl}
              alt={selectedNews?.head_line || "News image"}
              className="max-h-[90vh] max-w-full rounded-xl object-contain shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </main>
  );
}

NewsDetails.propTypes = {
  logoSrc: PropTypes.string,
  accent: PropTypes.string,
  pageTitle: PropTypes.string,
};
